import { useState } from "react";
import { Download, Share2, Apple, Smartphone, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useInstallPrompt } from "./useInstallPrompt";
import { trackEvent } from "@/lib/analytics";

type Props = {
  className?: string;
  variant?: "default" | "outline" | "cta" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg";
  label?: string;
  hideWhenUnavailable?: boolean;
};

export function InstallAppButton({
  className,
  variant = "cta",
  size = "lg",
  label = "Install App",
  hideWhenUnavailable = false,
}: Props) {
  const { canInstall, installed, isIOS, promptInstall } = useInstallPrompt();
  const [helpOpen, setHelpOpen] = useState(false);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isIOSChrome = /CriOS/.test(ua);
  const isIOSFirefox = /FxiOS/.test(ua);
  const isIOSEdge = /EdgiOS/.test(ua);
  const isIOSOther = isIOSChrome || isIOSFirefox || isIOSEdge;
  const otherBrowserName = isIOSChrome
    ? "Chrome"
    : isIOSFirefox
      ? "Firefox"
      : isIOSEdge
        ? "Edge"
        : "another browser";
  const isRealSafari = isIOS && !isIOSOther;
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // @ts-expect-error iOS Safari
      window.navigator.standalone === true);
  const isPrivateHint = typeof window !== "undefined" && window.location.protocol !== "https:";

  const checklist = [
    {
      ok: isRealSafari,
      label: isRealSafari
        ? "You are using Safari"
        : `You are using ${otherBrowserName} — open we-help.in in Safari instead`,
    },
    {
      ok: !isStandalone,
      label: isStandalone
        ? "Already installed — open it from your home screen"
        : "Running in a browser tab (ready to install)",
    },
    {
      ok: !isPrivateHint,
      label: isPrivateHint
        ? "Page is not on a secure (https) address — Add to Home Screen may be hidden"
        : "Secure (https) connection detected",
    },
    {
      ok: true,
      label: "Tap Share, then scroll the grey list down to find \"Add to Home Screen\"",
    },
    {
      ok: true,
      label: "Not there? Tap \"Edit Actions\" at the bottom of the share sheet and enable it",
    },
  ];

  if (installed) return null;
  if (!canInstall && !isIOS && hideWhenUnavailable) return null;

  const onClick = async () => {
    if (canInstall) {
      trackEvent("pwa_install_prompted", {});
      const r = await promptInstall();
      trackEvent(`pwa_install_${r.outcome}`, {});
      return;
    }
    setHelpOpen(true);
  };

  const title = isIOS ? "Install on iPhone / iPad" : "Install We-Help";

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={onClick}
        className={className}
      >
        <Download className="h-4 w-4" />
        {label}
      </Button>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isIOS ? <Apple className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
              {title}
            </DialogTitle>
            <DialogDescription>
              {isIOS
                ? "iOS doesn't support one-tap install. It only takes a moment:"
                : "If the browser install prompt does not appear, use your browser menu:"}
            </DialogDescription>
          </DialogHeader>
          {isIOS ? (
            <ol className="mt-2 space-y-3 text-sm text-foreground">
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">1.</span>
                <span>Open <strong>we-help.in</strong> in <strong>Safari</strong> (not Chrome).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">2.</span>
                <span>Tap the <Share2 className="inline h-4 w-4 align-text-bottom" /> <strong>Share</strong> button.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">3.</span>
                <span>Choose <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">4.</span>
                <span>Tap <strong>Add</strong>. The We-Help icon is now on your home screen.</span>
              </li>
            </ol>
          ) : (
            <ol className="mt-2 space-y-3 text-sm text-foreground">
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">1.</span>
                <span>Open <strong>we-help.in</strong> in Chrome or Edge.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">2.</span>
                <span>Open the browser menu.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">3.</span>
                <span>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-primary">4.</span>
                <span>Confirm <strong>Install</strong>. The We-Help shortcut opens like an app.</span>
              </li>
            </ol>
          )}

          {isIOS && (
            <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-sm font-semibold text-foreground">Not working? Quick checklist</p>
              <ul className="mt-2 space-y-2 text-sm">
                {checklist.map((item) => (
                  <li key={item.label} className="flex items-start gap-2">
                    {item.ok ? (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                    )}
                    <span className={item.ok ? "text-muted-foreground" : "text-foreground font-medium"}>
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InstallAppButton;